// Generated by ReScript, PLEASE EDIT WITH CARE

import * as I18n from "../../primitives/I18n.res.js";
import * as React from "react";
import * as Future from "rescript-future/src/Future.res.js";
import * as Pervasives from "@rescript/runtime/lib/es6/Pervasives.js";
import * as Form__Schema from "./Form__Schema.res.js";
import * as Form__Status from "./Form__Status.res.js";
import * as Stdlib_Option from "@rescript/runtime/lib/es6/Stdlib_Option.js";
import * as Form__Submission from "./Form__Submission.res.js";
import * as Primitive_object from "@rescript/runtime/lib/es6/Primitive_object.js";
import * as JsxRuntime from "react/jsx-runtime";
import * as ReactUpdateReducer from "../../primitives/ReactUpdateReducer.res.js";

function Make(Lenses) {
  let Schema = Form__Schema.Make(Lenses);
  let useReducer = (initialValues, schema, id) => ReactUpdateReducer.useWithMapState((state, action) => {
    if (typeof action !== "object") {
      if (action !== "ValidationRequested") {
        return {
          TAG: "Update",
          _0: {
            id: state.id,
            initialValues: state.initialValues,
            values: state.initialValues,
            status: "Pristine",
            submission: "Pending",
            schema: state.schema,
            validation: state.validation
          }
        };
      }
      let validation = Schema.validate(schema, state.values);
      let status;
      status = validation.TAG === "Ok" ? "Valid" : "Errored";
      if (Primitive_object.equal(validation, state.validation)) {
        return {
          TAG: "Update",
          _0: {
            id: state.id,
            initialValues: state.initialValues,
            values: state.values,
            status: status,
            submission: state.submission,
            schema: state.schema,
            validation: state.validation
          }
        };
      } else {
        return {
          TAG: "Update",
          _0: {
            id: state.id,
            initialValues: state.initialValues,
            values: state.values,
            status: status,
            submission: state.submission,
            schema: state.schema,
            validation: validation
          }
        };
      }
    } else {
      switch (action.TAG) {
        case "ResetRequested" :
          let values = action._0;
          return {
            TAG: "Update",
            _0: {
              id: state.id,
              initialValues: values,
              values: values,
              status: "Pristine",
              submission: state.submission,
              schema: state.schema,
              validation: state.validation
            }
          };
        case "FieldsValuesChanged" :
          let values$1 = action._0(state.values);
          let validation$1 = Schema.validate(schema, values$1);
          let status$1;
          status$1 = validation$1.TAG === "Ok" ? "Valid" : "Errored";
          return {
            TAG: "Update",
            _0: {
              id: state.id,
              initialValues: state.initialValues,
              values: values$1,
              status: status$1,
              submission: state.submission,
              schema: state.schema,
              validation: validation$1
            }
          };
        case "FieldValueChanged" :
          let field = action._0;
          let values$2 = Lenses.set(state.values, field, action._1(Lenses.get(state.values, field)));
          let validation$2 = Schema.validate(schema, values$2);
          let status$2;
          status$2 = validation$2.TAG === "Ok" ? "Valid" : "Errored";
          return {
            TAG: "Update",
            _0: {
              id: state.id,
              initialValues: state.initialValues,
              values: values$2,
              status: status$2,
              submission: state.submission,
              schema: state.schema,
              validation: validation$2
            }
          };
        case "SubmitButtonClicked" :
          let handler = action._0;
          let validation$3 = Schema.validate(schema, state.values);
          let status$3;
          status$3 = validation$3.TAG === "Ok" ? "Valid" : "Errored";
          let submission = Form__Status.isErrored(status$3) ? "Pending" : "Requested";
          return {
            TAG: "UpdateWithSideEffects",
            _0: {
              id: state.id,
              initialValues: state.initialValues,
              values: state.values,
              status: status$3,
              submission: submission,
              schema: state.schema,
              validation: validation$3
            },
            _1: self => {
              switch (status$3) {
                case "Pristine" :
                  break;
                case "Valid" :
                  Future.get(handler(state.id, state.values), result => {
                    let tmp;
                    tmp = result.TAG === "Ok" ? ({
                        TAG: "SubmissionSucceeded",
                        _0: result._0
                      }) : ({
                        TAG: "SubmissionFailed",
                        _0: result._0
                      });
                    self.dispatch(tmp);
                  });
                  break;
                case "Errored" :
                  self.dispatch({
                    TAG: "SubmissionFailed",
                    _0: I18n.t("There are some errors in the form, please correct them before trying to send it again.")
                  });
                  break;
              }
            }
          };
        case "SubmissionSucceeded" :
          return {
            TAG: "Update",
            _0: {
              id: state.id,
              initialValues: state.initialValues,
              values: state.values,
              status: state.status,
              submission: {
                TAG: "Succeeded",
                _0: action._0
              },
              schema: state.schema,
              validation: state.validation
            }
          };
        case "SubmissionFailed" :
          return {
            TAG: "Update",
            _0: {
              id: state.id,
              initialValues: state.initialValues,
              values: state.values,
              status: state.status,
              submission: {
                TAG: "Failed",
                _0: action._0
              },
              schema: state.schema,
              validation: state.validation
            }
          };
        case "IdAndInitialValuesUpdated" :
          let values$3 = action._1;
          return {
            TAG: "Update",
            _0: {
              id: action._0,
              initialValues: values$3,
              values: values$3,
              status: "Pristine",
              submission: "Pending",
              schema: state.schema,
              validation: state.validation
            }
          };
      }
    }
  }, () => ({
    id: id,
    initialValues: initialValues,
    values: initialValues,
    status: "Pristine",
    submission: "Pending",
    schema: schema,
    validation: Schema.validate(schema, initialValues)
  }));
  let useFormPropState = param => {
    let onSubmitSuccess = param.onSubmitSuccess;
    let onSubmitFailure = param.onSubmitFailure;
    let resetValuesAfterSubmission = param.resetValuesAfterSubmission;
    let initialValues = param.initialValues;
    let id = param.id;
    let match = useReducer(initialValues, param.schema, id);
    let dispatch = match[1];
    let state = match[0];
    React.useEffect(() => {
      if (state.id !== id) {
        dispatch({
          TAG: "IdAndInitialValuesUpdated",
          _0: id,
          _1: initialValues
        });
      }
    }, [id]);
    React.useEffect(() => {
      Stdlib_Option.forEach(onSubmitFailure, fn => {
        let failure = state.submission;
        if (typeof failure !== "object" || failure.TAG !== "Failed") {
          return;
        } else {
          return fn(failure._0);
        }
      });
      Stdlib_Option.forEach(onSubmitSuccess, fn => {
        let ok = state.submission;
        if (typeof ok !== "object" || ok.TAG !== "Succeeded") {
          return;
        } else {
          return fn(ok._0);
        }
      });
    }, [state.submission]);
    React.useEffect(() => {
      if (Form__Submission.isSucceeded(state.submission)) {
        let values = resetValuesAfterSubmission !== undefined && resetValuesAfterSubmission ? initialValues : state.values;
        dispatch({
          TAG: "ResetRequested",
          _0: values
        });
      }
    }, [state.submission]);
    return [
      state,
      dispatch
    ];
  };
  let context = React.createContext(undefined);
  let make = context.Provider;
  let makeProps = (value, children, param) => ({
    value: value,
    children: children
  });
  let ContextProvider = {
    make: make,
    makeProps: makeProps
  };
  let useFormContext = () => {
    let value = React.useContext(context);
    if (value !== undefined) {
      return value;
    } else {
      return Pervasives.failwith("Could not find form context value");
    }
  };
  let useFormState = () => useFormContext()[0];
  let useFormDispatch = () => useFormContext()[1];
  let useFormValues = () => useFormState().values;
  let useFormStatus = () => useFormState().status;
  let Form__Core$Make$Provider = props => JsxRuntime.jsx(make, {
    value: props.propState,
    children: props.children
  });
  let Provider = {
    make: Form__Core$Make$Provider
  };
  let Form__Core$Make$LegacyProvider = props => {
    let propState = useFormPropState({
      id: props.id,
      initialValues: props.initialValues,
      schema: props.schema,
      resetValuesAfterSubmission: props.resetValuesAfterSubmission,
      onSubmitFailure: props.onSubmitFailure,
      onSubmitSuccess: props.onSubmitSuccess
    });
    return JsxRuntime.jsx(Form__Core$Make$Provider, {
      children: props.children,
      propState: propState
    });
  };
  let LegacyProvider = {
    make: Form__Core$Make$LegacyProvider
  };
  let useField = (field, hideErrorOpt, hideRequiredOpt, enableTouchAfterValueChange, param) => {
    let hideError = hideErrorOpt !== undefined ? hideErrorOpt : false;
    let hideRequired = hideRequiredOpt !== undefined ? hideRequiredOpt : false;
    let match = useFormContext();
    let dispatch = match[1];
    let state = match[0];
    let value = Lenses.get(state.values, field);
    let match$1 = React.useState(() => false);
    let setFocused = match$1[1];
    let match$2 = React.useState(() => false);
    let setTouched = match$2[1];
    let touched = match$2[0];
    let errors = state.validation;
    let validation;
    if (errors.TAG === "Ok") {
      validation = {
        TAG: "Ok",
        _0: undefined
      };
    } else {
      let error = errors._0.find(error => Primitive_object.equal(error[0], {
        TAG: "Field",
        _0: field
      }));
      validation = error !== undefined ? ({
          TAG: "Error",
          _0: error[1]
        }) : ({
          TAG: "Ok",
          _0: undefined
        });
    }
    let match$3 = state.submission;
    let error$1;
    if (validation.TAG === "Ok") {
      error$1 = undefined;
    } else {
      let error$2 = validation._0;
      error$1 = hideError || typeof match$3 !== "object" && match$3 === "Pending" && !touched ? undefined : error$2;
    }
    let required = !hideRequired && Schema.required(state.schema, field);
    let onChange = React.useCallback(value => {
      if (touched === false && enableTouchAfterValueChange === true) {
        setTouched(param => true);
      }
      dispatch({
        TAG: "FieldValueChanged",
        _0: field,
        _1: _prev => value
      });
    }, [
      enableTouchAfterValueChange,
      touched
    ]);
    let onFocus = React.useCallback(() => setFocused(param => true), []);
    let onBlur = React.useCallback(() => {
      setTouched(param => true);
      setFocused(param => false);
    }, [
      touched,
      value
    ]);
    return {
      value: value,
      focused: match$1[0],
      error: error$1,
      required: required,
      onChange: onChange,
      onFocus: onFocus,
      onBlur: onBlur
    };
  };
  let useSubmit = handler => {
    let match = useFormContext();
    let submission = match[0].submission;
    let dispatch = match[1];
    let disabled = Form__Submission.isRequested(submission);
    let onSubmit = () => {
      if (!disabled) {
        return dispatch({
          TAG: "SubmitButtonClicked",
          _0: handler
        });
      }
    };
    return {
      disabled: disabled,
      submission: submission,
      onSubmit: onSubmit
    };
  };
  let useCancel = () => {
    let match = useFormContext();
    let dispatch = match[1];
    let disabled = Form__Submission.isRequested(match[0].submission);
    let onCancel = React.useCallback(() => dispatch("CancelButtonClicked"), []);
    return [
      disabled,
      onCancel
    ];
  };
  return {
    Status: undefined,
    Submission: undefined,
    Schema: Schema,
    useReducer: useReducer,
    useFormPropState: useFormPropState,
    context: context,
    ContextProvider: ContextProvider,
    useFormContext: useFormContext,
    useFormState: useFormState,
    useFormDispatch: useFormDispatch,
    useFormValues: useFormValues,
    useFormStatus: useFormStatus,
    Provider: Provider,
    LegacyProvider: LegacyProvider,
    useField: useField,
    useSubmit: useSubmit,
    useCancel: useCancel
  };
}

export {
  Make,
}
/* I18n Not a pure module */
